import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Filter,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { DsBadge, DsButton, DsEmptyState, DsIcon } from "../../design-system";
import { resolveDeskSlaStatus } from "../../utils/deskSlaStatus";
import {
  loadAttendanceInsights,
  saveAttendanceInsights,
} from "./attendance/attendanceInsightStore";
import {
  isCurrentCompanyRow,
  loadQuickActionRequests,
  saveQuickActionRequests,
} from "../../lib/attendanceDeskRequests";
import { getDeskTypeModule } from "../../utils/deskTypeCatalog";
import {
  formatDeskDisplayDate,
  formatDeskDisplayDateTime,
  getChecklistNameHighlight,
  getDeskActionButtons,
  getDeskAppliedFormFields,
  getDeskRemarksLabel,
  remarksRequiredForAction,
  resolveDeskActionKind,
} from "../../utils/deskActionMatrix";
import {
  loadActivityConsoleApiRows,
  runActivityConsoleDeskAction,
} from "../../utils/activityConsoleDesk";
import { deskRequestIdsEqual, ensureUniqueDeskRequests } from "../../utils/deskRowIdentity";
import { deskLatestActivityMs } from "../../utils/deskListOrder";
import { canShowRequestCancellation } from "../../utils/deskRequestCancellation";
import {
  displayLeaveActionLabel,
  isLeaveActionLabel,
  leaveLinkedCancellationCaption,
  matchesDeskSelectedType,
} from "../../utils/leaveRequestDesk";
import {
  resolveWorkflowSteps,
  workflowPendingDisplay,
} from "../../utils/workflowStepsBadge";
import { socket } from "../../services/websocket";
import { getAuthJson } from "../../utils/authStorage";

const MODULE_LABEL_BY_KEY = {
  employee: "Employee",
  attendance: "Attendance",
  payroll: "Payroll",
  hiring: "Hiring",
  communication: "Communication",
  helpdesk: "Helpdesk",
  assets: "Assets",
};

const resolveRowModuleLabel = (row) => {
  const fromType = getDeskTypeModule(row?.actionLabel);
  if (fromType && fromType !== "Other") return fromType;
  const key = String(row?.module || "").toLowerCase();
  return MODULE_LABEL_BY_KEY[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Other");
};
const ALL_TYPES = "All";
const STATUS_TABS = ["Pending", "Approved", "Rejected", "Undo"];
const STATUS_TAB_LABELS = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Undo: "Recall",
};
const REMARKS_MAX = 250;
const PAGE_SIZE = 50;

const safeParseList = (raw) => {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeStatusKey = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "approved") return "Approved";
  if (s === "rejected") return "Rejected";
  if (s === "undo" || s === "recalled" || s === "recall") return "Undo";
  if (s === "cancelled") return "Cancelled";
  return "Pending";
};

const statusLabelFromNext = (nextStatus) => {
  if (nextStatus === "Undo") return "Undo";
  return nextStatus;
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Display dates as `05 Sep 2026` (stored value unchanged). */
const formatDateDdMmYyyy = (value) => formatDeskDisplayDate(value) || "-";

/** Approval / submitted timestamps: `05 Sep 2026, 2:35 PM`. */
const formatDateTime = (value) => formatDeskDisplayDateTime(value) || "-";

const normPerson = (value) => String(value || "").trim().toLowerCase();

/**
 * Prefer API rows; drop true clones; uniquify colliding requestIds so
 * distinct checklist cards stay selectable.
 */
const dedupeConsoleRows = (rows) => {
  const sorted = [...rows].sort((a, b) => {
    const aa = a?.source === "api" ? 0 : 1;
    const bb = b?.source === "api" ? 0 : 1;
    return aa - bb;
  });
  return ensureUniqueDeskRequests(sorted);
};

/**
 * Activity trail shows approvers only — requester is already on the request header.
 * Drops submission / "Pending" steps whose actor is the employee who raised the request.
 * After a decision, drops superseded Pending/Current placeholders (avoids RM listed twice).
 */
const approvalTrailSteps = (request) => {
  const resolved = resolveWorkflowSteps(request);
  const steps = resolved.length
    ? resolved
    : Array.isArray(request?.workflowTrail)
      ? request.workflowTrail
      : [];
  if (!steps.length) return [];

  const requester = normPerson(request?.userName);
  const raisedBy = normPerson(request?.raisedByName);
  const requestStatus = String(request?.status || "").trim().toLowerCase();
  const decided =
    requestStatus === "approved" ||
    requestStatus === "rejected" ||
    steps.some((s) => {
      const st = String(s?.status || "").toLowerCase();
      return st === "approved" || st === "rejected";
    });

  return steps.filter((step) => {
    const actor = normPerson(step?.actor || step?.displayText);
    const status = String(step?.status || "").trim().toLowerCase();
    const note = String(step?.note || step?.reason || "").toLowerCase();

    // Once decided, only show Approve/Reject actions (not the earlier "waiting" row)
    if (decided && (status === "pending" || status === "current")) {
      return false;
    }

    // Self-raise / ESS submit: Pending with requester as actor
    if (status === "pending") {
      if (requester && actor === requester) return false;
      // Same person raised for themselves
      if (raisedBy && requester && raisedBy === requester && actor === raisedBy) return false;
      // Queue notes on the raise step (not an approval action)
      if (
        note.includes("queued for") ||
        note.includes("no reporting manager") ||
        note.includes("submitted")
      ) {
        return false;
      }
    }

    // Any other step where actor is clearly the subject employee (not an approver)
    if (requester && actor === requester && status !== "approved" && status !== "rejected") {
      return false;
    }

    return true;
  });
};

const toDayKey = (value) => {
  if (!value) return null;
  if (typeof value === "number") {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const submittedAtMs = (row) => {
  const v = row?.submittedAt ?? row?.updatedAt;
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const statusTone = (status) => {
  if (status === "Approved") return "success";
  if (status === "Rejected") return "danger";
  if (status === "Undo") return "info";
  if (status === "Cancelled") return "info";
  return "warning";
};

/** Outline-only initials for progressive approver bar (thin theme/status border). */
const approverAvatarClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "acknowledged") {
    return "border border-[hsl(var(--ds-success))] bg-transparent text-[hsl(var(--ds-success))]";
  }
  if (s === "rejected") {
    return "border border-[hsl(var(--ds-danger))] bg-transparent text-[hsl(var(--ds-danger))]";
  }
  if (s === "pending" || s === "current") {
    return "border border-[hsl(var(--ds-primary))] bg-transparent text-[hsl(var(--ds-primary))]";
  }
  if (s === "undo" || s === "recall") {
    return "border border-[hsl(var(--ds-info))] bg-transparent text-[hsl(var(--ds-info))]";
  }
  return "border border-[hsl(var(--ds-border))] bg-transparent text-[hsl(var(--ds-muted-fg))]";
};

const stepActorLabel = (step, request) => {
  const statusLower = String(step?.status || "").toLowerCase();
  const actorFallback =
    statusLower === "pending" || statusLower === "current" ? "Next" : null;
  const rawActor = step?.displayText || step?.actor || actorFallback;
  if (rawActor && !/^approver$/i.test(String(rawActor).trim())) return String(rawActor).trim();
  return (
    request?.firstDecision?.actorName ||
    request?.lastDecision?.actorName ||
    actorFallback ||
    "Unknown"
  );
};

/** Compact progressive bar: connector + outline initials (status border colours). */
const renderApproverProgressBar = (steps, request) => {
  if (!Array.isArray(steps) || !steps.length) return null;
  return (
    <div className="min-w-0" role="list" aria-label="Approval progress">
      <div className="flex items-center gap-0 overflow-x-auto py-0.5 admin-shell-scrollbar">
        {steps.map((step, idx) => {
          const actorLabel = stepActorLabel(step, request);
          const statusLower = String(step?.status || "").toLowerCase();
          const statusTip =
            statusLower === "undo"
              ? "Recall"
              : statusLower === "current"
                ? "Current"
                : step?.status || "";
          const title = [actorLabel, statusTip].filter(Boolean).join(" · ");
          const isLast = idx === steps.length - 1;
          return (
            <div key={`${step?.actedAt || idx}-${statusTip}`} className="flex items-center" role="listitem">
              <span
                title={title}
                aria-label={title}
                className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold tracking-tight leading-none ${approverAvatarClass(
                  step?.status
                )}`}
              >
                {initialsFromName(actorLabel)}
              </span>
              {!isLast ? (
                <span
                  className={`mx-1 h-px w-3.5 shrink-0 sm:w-4 ${
                    statusLower === "approved" || statusLower === "acknowledged"
                      ? "bg-[hsl(var(--ds-success))]/45"
                      : statusLower === "rejected"
                        ? "bg-[hsl(var(--ds-danger))]/40"
                        : statusLower === "pending" || statusLower === "current"
                          ? "bg-[hsl(var(--ds-primary))]/35"
                          : "bg-[hsl(var(--ds-border))]"
                  }`}
                  aria-hidden
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const initialsFromName = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const formatRelativeTime = (value) => {
  const ms = submittedAtMs({ submittedAt: value });
  if (!ms) return "";
  const diff = Date.now() - ms;
  if (diff < 0) return formatDateTime(value);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateDdMmYyyy(value);
};

const filterControlCls =
  "h-9 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 text-xs text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))]";

export default function ActivityConsole() {
  const [searchParams] = useSearchParams();
  const moduleParam = String(searchParams.get("module") || "all").toLowerCase();
  const focusModule =
    moduleParam === "employee" ||
    moduleParam === "attendance" ||
    moduleParam === "payroll"
      ? moduleParam
      : null;
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const visibleStatus = STATUS_TABS.includes(selectedStatus)
    ? selectedStatus
    : selectedStatus === "Cancelled"
      ? "Approved"
      : "Pending";
  const [selectedType, setSelectedType] = useState(ALL_TYPES);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);
  const [slaClock, setSlaClock] = useState(() => Date.now());
  const [actionModal, setActionModal] = useState({
    open: false,
    nextStatus: "",
    title: "",
    remarks: "",
    requestIds: [],
  });
  const [apiRows, setApiRows] = useState([]);

  const refresh = useCallback(() => setRefreshTick((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { hydrateCompOffOtDeskFromServer } = await import(
          "../../lib/compOffOtNotify"
        );
        await hydrateCompOffOtDeskFromServer();
        if (cancelled) return;
        const before = loadQuickActionRequests();
        const { enrichPendingDeskRequestsWithRmNames } = await import(
          "../../lib/attendanceDeskRequests"
        );
        await enrichPendingDeskRequestsWithRmNames(before);
        if (!cancelled) setRefreshTick((v) => v + 1);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await loadActivityConsoleApiRows();
        if (!cancelled) setApiRows(rows);
      } catch {
        if (!cancelled) setApiRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    const onLocalChanged = () => refresh();
    const onSocketDesk = (payload) => {
      try {
        const me = getAuthJson("currentUser");
        const myCompanyId = me?.companyId ?? me?.company_id;
        if (
          payload?.companyId != null &&
          myCompanyId != null &&
          Number(payload.companyId) !== Number(myCompanyId)
        ) {
          return;
        }
      } catch {
        /* ignore auth parse */
      }
      refresh();
    };
    window.addEventListener("admin:leave-request-changed", onLocalChanged);
    window.addEventListener("admin:quick-action-submitted", onLocalChanged);
    window.addEventListener("admin:lifecycle-request-changed", onLocalChanged);
    window.addEventListener("admin:onboarding-submitted", onLocalChanged);
    if (socket?.on) {
      socket.on("leave-request-updated", onSocketDesk);
      socket.on("workflow-desk-updated", onSocketDesk);
    }
    return () => {
      window.removeEventListener("admin:leave-request-changed", onLocalChanged);
      window.removeEventListener("admin:quick-action-submitted", onLocalChanged);
      window.removeEventListener("admin:lifecycle-request-changed", onLocalChanged);
      window.removeEventListener("admin:onboarding-submitted", onLocalChanged);
      if (socket?.off) {
        socket.off("leave-request-updated", onSocketDesk);
        socket.off("workflow-desk-updated", onSocketDesk);
      }
    };
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => setSlaClock(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setItemSearchQuery("");
    setSelectedId(null);
    setSelectedType(ALL_TYPES);
  }, [selectedStatus]);

  const allRows = useMemo(() => {
    const quickRequests = loadQuickActionRequests();
    const quickByRequestId = new Map(
      quickRequests.map((r) => [String(r.requestId), r])
    );

    // Central tracker: every local desk workflow (RH, OD, OT, Change Shift, …)
    // appears here so Admin can see and act — not only the legacy insight whitelist.
    const quickDeskRows = quickRequests
      .filter((req) => {
        if (!req?.requestId || !req?.actionLabel) return false;
        // Leave is loaded from the API desk; skip local Leave stubs.
        if (isLeaveActionLabel(req.actionLabel) || req.deskMode === "leave") return false;
        return true;
      })
      .map((req) => {
        const status = normalizeStatusKey(req.status);
        const formData = { ...(req.formData || {}) };
        const employeeCode =
          req.employeeCode || formData.employeeCode || "";
        return {
          id: req.requestId,
          requestId: req.requestId,
          module: "attendance",
          actionLabel: req.actionLabel,
          userName: req.userName || formData.employeeName || "-",
          employeeCode,
          userEmail: req.userEmail || "",
          status,
          pendingWith:
            status === "Pending"
              ? req.pendingAssigneeDisplay ||
                req.pendingWith ||
                req.pendingLabel ||
                "Reporting Manager"
              : null,
          pendingAssigneeDisplay: req.pendingAssigneeDisplay || null,
          pendingLabel: req.pendingLabel || null,
          pendingRole: req.pendingRole || null,
          submittedAt: req.submittedAt || null,
          updatedAt: req.updatedAt || null,
          formData,
          workflowTrail: req.workflowTrail || [],
          lastDecision: req.lastDecision || formData.lastDecision || null,
          firstDecision: req.firstDecision || formData.firstDecision || null,
          raisedByName: req.raisedByName || null,
          reportingManagerEmployeeId: req.reportingManagerEmployeeId || null,
          reportingManagerName: req.reportingManagerName || null,
          employeeId: req.employeeId || null,
          slaDays: 3,
          source: req.source === "api" ? "api" : "local",
          serverDeskId: req.serverDeskId || null,
          canAct: true,
          canApprove: true,
          canRequestCancellation: req.canRequestCancellation === true,
          requestType: req.requestType || null,
          parentQuickRequestId: req.parentQuickRequestId || null,
          cancelledAfterApproval: !!req.cancelledAfterApproval,
          openCancellationRequestId: req.openCancellationRequestId || null,
        };
      });

    const attendanceInsights = loadAttendanceInsights()
      .filter(isCurrentCompanyRow)
      .filter((row) => {
        // Leave is loaded from the API desk; skip local insight stubs (incl. "Leave Request").
        const insightAction = String(row.action || row.actionLabel || "");
        if (isLeaveActionLabel(insightAction)) return false;
        // Prefer live quick-desk row when both exist
        const rid = String(row.requestId || row.id || "");
        return rid && !quickByRequestId.has(rid);
      })
      .map((row) => {
        const req = quickByRequestId.get(String(row.requestId));
        const status = normalizeStatusKey(row.status || req?.status);
        const formData = { ...(req?.formData || {}), ...(row.formData || {}) };
        const employeeCode =
          row.employeeCode || req?.employeeCode || formData.employeeCode || "";
        const employeeName =
          row.employeeName || req?.userName || formData.employeeName || "-";
        return {
          id: row.id || row.requestId,
          requestId: row.requestId || row.id,
          module: "attendance",
          actionLabel: displayLeaveActionLabel(
            row.action || row.actionLabel || "Attendance request"
          ),
          userName: employeeName,
          employeeCode,
          userEmail: req?.userEmail || "",
          status,
          pendingWith:
            status === "Pending"
              ? row.pendingAt ||
                row.pendingWith ||
                req?.pendingAssigneeDisplay ||
                req?.pendingWith ||
                "Reporting Manager"
              : null,
          pendingAssigneeDisplay:
            req?.pendingAssigneeDisplay || row.pendingAssigneeDisplay || null,
          submittedAt: row.submittedAt || req?.submittedAt || null,
          updatedAt: row.updatedAt || req?.updatedAt || null,
          formData,
          workflowTrail: req?.workflowTrail || row.workflowTrail || [],
          raisedByName: req?.raisedByName || row.raisedByName || null,
          slaDays: 3,
          source: "local",
          canAct: true,
          canApprove: true,
        };
      });

    const employeeInsights = safeParseList(
      localStorage.getItem("employeeActionInsightsLog")
    )
      .filter(isCurrentCompanyRow)
      .map((row) => {
      const status = normalizeStatusKey(row.status);
      return {
        id: row.id || row.requestId,
        requestId: row.requestId || row.id,
        module: "employee",
        actionLabel: row.action || "Employee task",
        userName: row.employeeName || row.employee || "-",
        employeeCode: row.employeeCode || "",
        userEmail: row.userEmail || "",
        status,
        pendingWith:
          status === "Pending" ? row.pendingAt || row.pendingWith || "Workflow" : null,
        submittedAt: row.submittedAt || row.createdAt || row.loggedAt || null,
        updatedAt: row.updatedAt || row.lastUpdatedAt || null,
        formData: row.formData || {},
        workflowTrail: row.workflowTrail || [],
        slaDays: 3,
        source: "local",
        canAct: true,
        canApprove: true,
      };
    });

    // Prefer live API rows over duplicate local employee / attendance insight ids
    const apiIds = new Set(apiRows.map((r) => String(r.requestId)));
    const localQuick = quickDeskRows.filter((r) => !apiIds.has(String(r.requestId)));
    const localEmployee = employeeInsights.filter((r) => !apiIds.has(String(r.requestId)));
    const localAttendance = attendanceInsights.filter(
      (r) => !apiIds.has(String(r.requestId))
    );

    return dedupeConsoleRows([
      ...apiRows,
      ...localQuick,
      ...localAttendance,
      ...localEmployee,
    ]);
  }, [refreshTick, apiRows]);

  const moduleRows = useMemo(() => {
    if (!focusModule) return allRows;
    const want = MODULE_LABEL_BY_KEY[focusModule] || focusModule;
    return allRows.filter((row) => {
      const key = String(row?.module || "").toLowerCase();
      if (key === focusModule) return true;
      return resolveRowModuleLabel(row) === want;
    });
  }, [allRows, focusModule]);

  useEffect(() => {
    setSelectedType(ALL_TYPES);
    setSelectedId(null);
    setSelectedIds([]);
  }, [focusModule]);

  /** Search + date filtered rows, before the request-type badge filter. */
  const filteredByDateSearch = useMemo(() => {
    const q = itemSearchQuery.trim().toLowerCase();
    return moduleRows.filter((row) => {
      if (q) {
        const haystack = [
          row.userName,
          row.userEmail,
          row.employeeCode,
          row.actionLabel,
          row.pendingWith,
          row.module,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (dateFrom || dateTo) {
        const day =
          toDayKey(row.submittedAt) ||
          toDayKey(row.formData?.date) ||
          toDayKey(row.formData?.fromDate) ||
          null;
        if (!day) return false;
        if (dateFrom && day < dateFrom) return false;
        if (dateTo && day > dateTo) return false;
      }
      return true;
    });
  }, [moduleRows, itemSearchQuery, dateFrom, dateTo]);

  /** Request-type badges: All first, then types by pending count (desc). */
  const typeBadges = useMemo(() => {
    const pendingRows = filteredByDateSearch.filter((row) => row.status === "Pending");
    const labels = new Set();
    moduleRows.forEach((row) => {
      if (row.actionLabel) labels.add(row.actionLabel);
    });
    const types = Array.from(labels)
      .map((label) => ({
        value: label,
        label,
        pending: pendingRows.filter((row) => matchesDeskSelectedType(row.actionLabel, label))
          .length,
      }))
      .sort((x, y) => y.pending - x.pending || x.label.localeCompare(y.label));
    return [{ value: ALL_TYPES, label: ALL_TYPES, pending: pendingRows.length }, ...types];
  }, [moduleRows, filteredByDateSearch]);

  const filteredByTypeDateSearch = useMemo(
    () =>
      selectedType === ALL_TYPES
        ? filteredByDateSearch
        : filteredByDateSearch.filter((row) =>
            matchesDeskSelectedType(row.actionLabel, selectedType)
          ),
    [filteredByDateSearch, selectedType]
  );

  const statusCounts = useMemo(() => {
    const out = { Pending: 0, Approved: 0, Rejected: 0, Undo: 0 };
    filteredByTypeDateSearch.forEach((row) => {
      out[row.status] = (out[row.status] || 0) + 1;
    });
    return out;
  }, [filteredByTypeDateSearch]);

  const visibleRequests = useMemo(() => {
    const rows = filteredByTypeDateSearch.filter((row) => row.status === visibleStatus);
    rows.sort((a, b) => deskLatestActivityMs(b) - deskLatestActivityMs(a));
    return rows;
  }, [filteredByTypeDateSearch, visibleStatus]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => visibleRequests.some((r) => r.requestId === id))
    );
  }, [visibleRequests]);

  // Pagination is UI-only: reset to the first page whenever the result set changes.
  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [visibleStatus, selectedType, itemSearchQuery, dateFrom, dateTo, focusModule]);

  const displayedRequests = useMemo(
    () => visibleRequests.slice(0, visibleLimit),
    [visibleRequests, visibleLimit]
  );
  const hasMore = visibleRequests.length > displayedRequests.length;

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  // Select-all applies to the rendered page only, so off-screen rows are never silently selected.
  const allVisibleSelected =
    displayedRequests.length > 0 &&
    displayedRequests.every((r) => selectedIdSet.has(r.requestId));

  const toggleRequestSelection = (requestId) => {
    setSelectedIds((prev) =>
      prev.includes(requestId) ? prev.filter((id) => id !== requestId) : [...prev, requestId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !displayedRequests.some((r) => r.requestId === id))
      );
      return;
    }
    const ids = displayedRequests.map((r) => r.requestId);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const canBulkAct = visibleStatus === "Pending" && selectedIds.length > 0;

  const openActionModal = (nextStatus, title, requestIds) => {
    setActionModal({
      open: true,
      nextStatus,
      title,
      remarks: "",
      requestIds: requestIds || [],
    });
  };

  const closeActionModal = () => {
    setActionModal({
      open: false,
      nextStatus: "",
      title: "",
      remarks: "",
      requestIds: [],
    });
  };

  const resolveActorName = () => {
    try {
      const me = getAuthJson("currentUser");
      const name = String(me?.name || me?.user?.name || "").trim();
      if (name) return name;
    } catch {
      /* ignore */
    }
    return "Admin";
  };

  const applyAttendanceAction = async (requestId, nextStatus, note) => {
    if (!requestId) return;
    if (nextStatus === "CancelRequested") {
      const requests = loadQuickActionRequests();
      const matched = requests.find((row) => row.requestId === requestId);
      const { attendanceService } = await import("../../services/api");
      const id = matched?.serverDeskId || requestId;
      const res = await attendanceService.requestDeskCancellation(id, note);
      toast.success(
        res?.autoApproved ? "Request cancelled" : "Cancellation request submitted"
      );
      try {
        const { hydrateCompOffOtDeskFromServer } = await import(
          "../../lib/compOffOtNotify"
        );
        await hydrateCompOffOtDeskFromServer();
      } catch {
        /* ignore */
      }
      return;
    }
    const now = Date.now();
    const label = statusLabelFromNext(nextStatus);
    const actor = resolveActorName();

    const requests = loadQuickActionRequests();
    const matched = requests.find((row) => row.requestId === requestId);
    if (
      (nextStatus === "Approved" || nextStatus === "Rejected") &&
      String(matched?.status || "") === nextStatus
    ) {
      throw new Error(`This request is already ${String(nextStatus).toLowerCase()}.`);
    }
    const nextRequests = requests.map((row) => {
      if (row.requestId !== requestId) return row;
      const trail = Array.isArray(row.workflowTrail) ? [...row.workflowTrail] : [];
      const openIdx = trail.findIndex((s) => {
        const st = String(s?.status || "").toLowerCase();
        return st === "current" || st === "pending";
      });
      const entry = {
        status: nextStatus,
        actor,
        displayText: actor,
        actedAt: now,
        note,
      };
      if (openIdx >= 0) {
        trail[openIdx] = { ...trail[openIdx], ...entry };
      } else {
        trail.push(entry);
      }
      return {
        ...row,
        status: label,
        pendingWith: null,
        pendingAssigneeDisplay: null,
        updatedAt: now,
        workflowTrail: trail,
        actionRemarks: note,
        actedByName: actor,
      };
    });
    saveQuickActionRequests(nextRequests);

    const insights = loadAttendanceInsights();
    const nextInsights = insights.map((row) => {
      if (row.requestId !== requestId) return row;
      const trail = Array.isArray(row.workflowTrail) ? [...row.workflowTrail] : [];
      const openIdx = trail.findIndex((s) => {
        const st = String(s?.status || "").toLowerCase();
        return st === "current" || st === "pending";
      });
      const entry = {
        status: nextStatus,
        actor,
        displayText: actor,
        actedAt: now,
        note,
      };
      if (openIdx >= 0) {
        trail[openIdx] = { ...trail[openIdx], ...entry };
      } else {
        trail.push(entry);
      }
      return {
        ...row,
        status: label,
        pendingAt: "-",
        pendingWith: "-",
        updatedAt: now,
        workflowTrail: trail,
        actionRemarks: note,
        actedByName: actor,
      };
    });
    saveAttendanceInsights(nextInsights);

    // Keep Restricted Holiday / Comp Off / OT engine status in sync when Admin acts from Console
    if (nextStatus === "Approved" || nextStatus === "Rejected" || nextStatus === "Undo") {
      const label = String(matched?.actionLabel || "");
      try {
        if (nextStatus === "Undo") {
          const { syncDeskRequestRecall } = await import("../../lib/compOffOtNotify");
          await syncDeskRequestRecall({ ...matched, actionRemarks: note }, note);
        } else if (label === "Restricted Holiday") {
          const { syncRestrictedHolidayDecisionAndNotify } = await import(
            "../../lib/restrictedHolidayNotify"
          );
          await syncRestrictedHolidayDecisionAndNotify(matched, nextStatus);
        } else if (
          label === "Comp Off" ||
          label === "Comp Off Work" ||
          label === "Comp Off Credit"
        ) {
          const { syncCompOffDecision } = await import("../../lib/compOffOtNotify");
          await syncCompOffDecision(matched, nextStatus);
        } else if (label === "Overtime") {
          const { syncOvertimeDecision } = await import("../../lib/compOffOtNotify");
          await syncOvertimeDecision(matched, nextStatus);
        } else if (label === "Regularise checks") {
          const { syncRegulariseDecision } = await import("../../lib/compOffOtNotify");
          await syncRegulariseDecision(matched, nextStatus);
        } else if (
          label === "Change WO" ||
          label === "Change Shift" ||
          label === "Short Leave" ||
          label === "On Duty" ||
          label === "Attendance Lock" ||
          label === "Attendance Unlock" ||
          label === "Attendance Finalization"
        ) {
          const { attendanceService } = await import("../../services/api");
          const { getAuthJson } = await import("../../utils/authStorage");
          const id = matched?.serverDeskId || matched?.requestId;
          const currentUser = getAuthJson("currentUser", null);
          const firstName = currentUser?.name || "";
          const lastName = currentUser?.lastName || "";
          const actorName = [firstName, lastName].filter(Boolean).join(" ") || null;
          if (!id) {
            throw new Error(`${label} request is missing a server id`);
          }
          await attendanceService.updateDeskQuickRequestStatus(id, nextStatus, {
            reason: note || matched?.actionRemarks || null,
            actorName,
          });
        }
        const { hydrateCompOffOtDeskFromServer } = await import("../../lib/compOffOtNotify");
        await hydrateCompOffOtDeskFromServer();
      } catch (err) {
        try {
          const { hydrateCompOffOtDeskFromServer } = await import("../../lib/compOffOtNotify");
          await hydrateCompOffOtDeskFromServer();
        } catch {
          /* ignore */
        }
        throw err;
      }
    }
  };

  const applyEmployeeAction = (requestId, nextStatus, note) => {
    const now = Date.now();
    const label = statusLabelFromNext(nextStatus);
    const actor = resolveActorName();
    const current = safeParseList(localStorage.getItem("employeeActionInsightsLog"));
    const next = current.map((row) => {
      const same =
        String(row?.requestId) === String(requestId) || String(row?.id) === String(requestId);
      if (!same) return row;
      const trail = Array.isArray(row.workflowTrail) ? [...row.workflowTrail] : [];
      trail.push({ status: nextStatus, actor, actedAt: now, note });
      return {
        ...row,
        status: label,
        pendingAt: "-",
        pendingWith: "-",
        updatedAt: now,
        workflowTrail: trail,
        actionRemarks: note,
        actedByName: actor,
      };
    });
    localStorage.setItem("employeeActionInsightsLog", JSON.stringify(next));
  };

  const confirmAction = async () => {
    const remarks = (actionModal.remarks || "").trim();
    const ids = actionModal.requestIds || [];
    if (!ids.length) return;

    const sample = allRows.find((r) => r.requestId === ids[0]);
    const needRemarks = remarksRequiredForAction(sample, actionModal.nextStatus);
    if (needRemarks && !remarks) {
      toast.error(`${getDeskRemarksLabel(sample) || "Remarks"} are mandatory.`);
      return;
    }
    if (remarks.length > REMARKS_MAX) {
      toast.error(`Comments must be ${REMARKS_MAX} characters or less.`);
      return;
    }

    let ok = 0;
    for (const requestId of ids) {
      const row = allRows.find((r) => r.requestId === requestId);
      if (!row) continue;
      try {
        if (row.source === "api") {
          const result = await runActivityConsoleDeskAction(
            row,
            actionModal.nextStatus,
            remarks
          );
          if (result?.message) toast.success(result.message);
          ok += 1;
        } else if (row.module === "attendance") {
          await applyAttendanceAction(requestId, actionModal.nextStatus, remarks);
          ok += 1;
        } else {
          applyEmployeeAction(requestId, actionModal.nextStatus, remarks);
          ok += 1;
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Action failed");
      }
    }

    if (ok && sample?.source !== "api") {
      toast.success(`${actionModal.title} saved with remarks.`);
    }
    try {
      const { hydrateCompOffOtDeskFromServer } = await import("../../lib/compOffOtNotify");
      await hydrateCompOffOtDeskFromServer();
    } catch {
      /* ignore */
    }
    try {
      window.dispatchEvent(
        new CustomEvent("admin:quick-action-status", {
          detail: {
            status: actionModal.nextStatus,
            requestIds: ids,
            employeeId: sample?.employeeId || null,
            actionLabel: sample?.actionLabel || null,
          },
        })
      );
      window.dispatchEvent(
        new CustomEvent("admin:quick-action-submitted", {
          detail: {
            status: actionModal.nextStatus,
            requestIds: ids,
            employeeId: sample?.employeeId || null,
            actionLabel: sample?.actionLabel || null,
          },
        })
      );
    } catch {
      /* ignore */
    }
    setSelectedIds([]);
    closeActionModal();
    refresh();
  };

  const appliedFields = (request) => getDeskAppliedFormFields(request);

  const renderRequestCard = (request) => {
    const isFocused = deskRequestIdsEqual(selectedId, request.requestId);
    const isChecked = selectedIdSet.has(request.requestId);
    const heading = request.actionLabel || "Request";
    const actionKind = resolveDeskActionKind(request);
    const checklistName = getChecklistNameHighlight(request);
    const typeText =
      (actionKind === "checklist" || actionKind === "letter") &&
      checklistName &&
      checklistName !== heading
        ? `${heading} - ${checklistName}`
        : heading;
    const kindBadgeLabel =
      actionKind === "letter" ? "Letter" : actionKind === "checklist" ? "Checklist" : null;
    const isLeave = isLeaveActionLabel(request.actionLabel) || request.deskMode === "leave";
    const sla = request.status === "Pending" ? resolveDeskSlaStatus(request, slaClock) : null;
    const showSla = sla?.key === "overdue" || sla?.key === "due_soon";
    const pendingWith = request.status === "Pending" ? workflowPendingDisplay(request) : null;
    const statusText =
      request.status === "Undo"
        ? "Recall"
        : request.status === "Pending"
          ? String(request.apiStatus || "").toLowerCase() === "draft"
            ? "Draft"
            : "Waiting"
          : request.status;
    const relMs =
      request.status === "Approved" || request.status === "Rejected"
        ? request.firstDecision?.actedAt ||
          request.workflowTrail?.find((s) => {
            const st = String(s?.status || "").toLowerCase();
            return st === "approved" || st === "rejected";
          })?.actedAt ||
          request.lastDecision?.actedAt ||
          request.submittedAt ||
          request.updatedAt
        : request.submittedAt || request.updatedAt;
    const rel = formatRelativeTime(relMs);
    const trailSteps = approvalTrailSteps(request);

    const infoBlockCls =
      "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 p-2.5";

    const renderBody = () => {
      const fd = request.formData || {};
      if (isLeave) {
        const duration = String(fd.leaveDuration || "")
          .replace(/[_-]+/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        const from = fd.fromDate || "";
        const to = fd.toDate || "";
        const sameDay =
          from && to && String(from).slice(0, 10) === String(to).slice(0, 10);
        const dateLine = sameDay
          ? formatDeskDisplayDate(from)
          : [from, to]
              .filter(Boolean)
              .map((d) => formatDeskDisplayDate(d))
              .filter(Boolean)
              .join(" → ");
        return (
          <div className={infoBlockCls}>
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
                {fd.leaveType || "Leave"}
              </span>
              {duration ? (
                <span className="text-xs text-[hsl(var(--ds-muted-fg))]">· {duration}</span>
              ) : null}
            </div>
            {dateLine ? (
              <p className="mt-0.5 text-xs tabular-nums text-[hsl(var(--ds-fg))]">{dateLine}</p>
            ) : null}
            {fd.reason ? (
              <p className="mt-1.5 line-clamp-3 break-words text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                {fd.reason}
              </p>
            ) : null}
            {fd.notes ? (
              <p className="mt-1 line-clamp-2 break-words text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Note: {fd.notes}
              </p>
            ) : null}
          </div>
        );
      }

      if (request.actionLabel === "Restricted Holiday") {
        const dateLine = formatDeskDisplayDate(fd.date || fd.holidayDate || "");
        return (
          <div className={infoBlockCls}>
            <p className="truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
              {fd.holidayName || "Restricted Holiday"}
            </p>
            {dateLine ? (
              <p className="mt-0.5 text-xs tabular-nums text-[hsl(var(--ds-fg))]">{dateLine}</p>
            ) : null}
            {fd.reason ? (
              <p className="mt-1.5 line-clamp-3 break-words text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                {fd.reason}
              </p>
            ) : null}
            {fd.notes ? (
              <p className="mt-1 line-clamp-2 break-words text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Note: {fd.notes}
              </p>
            ) : null}
          </div>
        );
      }

      if (request.deskMode === "checklist" || /Task$/i.test(String(request.actionLabel || ""))) {
        let dueLine = "";
        if (fd.dueAt) {
          const due = new Date(fd.dueAt);
          dueLine = Number.isNaN(due.getTime()) ? String(fd.dueAt) : formatDateTime(fd.dueAt);
        }
        const comments = fd.completedComments || fd.sentBackComments || fd.rejectedComments || "";
        return (
          <div className={infoBlockCls}>
            <p className="line-clamp-2 break-words text-sm font-semibold text-[hsl(var(--ds-fg))]">
              {checklistName || fd.taskName || "Onboarding task"}
            </p>
            {dueLine ? (
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-fg))]">Due {dueLine}</p>
            ) : null}
            {comments ? (
              <p className="mt-1.5 line-clamp-3 break-words text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                {comments}
              </p>
            ) : null}
          </div>
        );
      }

      if (
        request.actionLabel === "Employee Onboarding Approval" ||
        request.actionLabel === "Employee Onboarding" ||
        request.lifecycleKind === "onboarding"
      ) {
        const orgLine = [fd.department, fd.designation, fd.branch]
          .filter((v) => v != null && String(v).trim() !== "")
          .join(" · ");
        const doj = fd.dateOfJoin ? formatDateDdMmYyyy(fd.dateOfJoin) : "";
        return (
          <div className={infoBlockCls}>
            <p className="truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
              {fd.employmentType || "Onboarding"}
            </p>
            {orgLine ? (
              <p className="mt-0.5 line-clamp-2 break-words text-xs text-[hsl(var(--ds-muted-fg))]">{orgLine}</p>
            ) : null}
            {doj && doj !== "-" ? (
              <p className="mt-1 text-xs tabular-nums text-[hsl(var(--ds-fg))]">Date of join {doj}</p>
            ) : null}
            {fd.reportingManager ? (
              <p className="mt-1 line-clamp-1 break-words text-xs text-[hsl(var(--ds-muted-fg))]">
                Reporting manager: {fd.reportingManager}
              </p>
            ) : null}
            {fd.policyName || request.policyName ? (
              <p className="mt-1 line-clamp-1 break-words text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Policy: {fd.policyName || request.policyName}
              </p>
            ) : null}
          </div>
        );
      }

      const fields = appliedFields(request);
      if (!fields.length) {
        return (
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No form details on this request.</p>
        );
      }
      return (
        <div className={infoBlockCls}>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
            {fields.map((field) => (
              <div
                key={field.key}
                className={`min-w-0 ${
                  field.key === "reason" ||
                  field.key === "notes" ||
                  field.key === "completedComments" ||
                  field.key === "sentBackComments" ||
                  field.key === "ticketSubject"
                    ? "col-span-2"
                    : ""
                }`}
              >
                <dt className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  {field.label}
                </dt>
                <dd className="mt-0.5 line-clamp-3 whitespace-pre-wrap break-words text-xs text-[hsl(var(--ds-fg))]">
                  {field.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      );
    };

    const actionButtons = request.status === "Pending" ? getDeskActionButtons(request) : [];

    return (
      <article
        key={String(request.requestId)}
        aria-current={isFocused}
        onClick={() => setSelectedId(request.requestId)}
        className={`flex min-w-0 flex-col gap-3 rounded-lg border bg-[hsl(var(--ds-surface))] p-3 shadow-[var(--ds-shadow-card)] transition ${
          isFocused
            ? "border-[hsl(var(--ds-primary))] ring-1 ring-[hsl(var(--ds-primary))]/30"
            : isChecked
              ? "border-[hsl(var(--ds-primary))]/50"
              : "border-[hsl(var(--ds-border))] hover:border-[hsl(var(--ds-primary))]/40"
        }`}
      >
        <header className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={isChecked}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              toggleRequestSelection(request.requestId);
            }}
            className="mt-1.5 rounded border-[hsl(var(--ds-border))]"
            aria-label={`Select ${request.userName || "request"} for bulk action`}
          />
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ds-muted))] text-[10px] font-semibold text-[hsl(var(--ds-fg))]"
            aria-hidden
          >
            {initialsFromName(request.userName)}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-[hsl(var(--ds-fg))]" title={request.userName}>
              {request.userName || "-"}
            </h3>
            <p className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))]">
              {request.employeeCode || request.userEmail || "-"}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <DsBadge tone={statusTone(request.status)}>{statusText}</DsBadge>
            {showSla ? (
              <span className={`text-[10px] font-semibold ${sla.className}`}>{sla.label}</span>
            ) : null}
          </div>
        </header>

        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="min-w-0 truncate text-xs font-semibold text-[hsl(var(--ds-primary))]" title={typeText}>
            {typeText}
          </span>
          {kindBadgeLabel ? (
            <DsBadge
              tone={actionKind === "letter" ? "warning" : "info"}
              className="shrink-0 !px-1.5 !py-0 !text-[9px] !leading-4"
            >
              {kindBadgeLabel}
            </DsBadge>
          ) : null}
          {rel ? (
            <span className="ml-auto text-[10px] text-[hsl(var(--ds-muted-fg))]">{rel}</span>
          ) : null}
        </div>
        {leaveLinkedCancellationCaption(request.linkedCancellation) ? (
          <p
            className={`truncate text-[11px] font-medium ${
              String(request.linkedCancellation?.status || "").toLowerCase() === "approved"
                ? "text-[hsl(var(--ds-danger))]"
                : "text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            {leaveLinkedCancellationCaption(request.linkedCancellation)}
          </p>
        ) : null}

        <div className="min-w-0 flex-1 space-y-2.5">
          {renderBody()}
          <div className="border-t border-[hsl(var(--ds-border))] pt-2">
            {trailSteps.length
              ? renderApproverProgressBar(trailSteps, request)
              : pendingWith
                ? renderApproverProgressBar(
                    [{ status: "current", actor: pendingWith, displayText: pendingWith }],
                    request
                  )
                : null}
          </div>
        </div>

        <footer
          className="border-t border-[hsl(var(--ds-border))] pt-2.5"
          onClick={(event) => event.stopPropagation()}
        >
          {request.status === "Pending" ? (
            <div className="flex flex-wrap justify-end gap-1.5">
              {actionButtons.map((btn) => {
                const variant =
                  btn.status === "Approved" ||
                  btn.status === "Acknowledged" ||
                  btn.status === "Submitted"
                    ? "primary"
                    : btn.status === "Rejected"
                      ? "danger"
                      : "outline";
                return (
                  <DsButton
                    key={`${btn.status}-${btn.label}`}
                    type="button"
                    variant={variant}
                    size="sm"
                    onClick={() => openActionModal(btn.status, btn.label, [request.requestId])}
                  >
                    {btn.label}
                  </DsButton>
                );
              })}
              {!actionButtons.length ? (
                <span className="text-xs text-[hsl(var(--ds-muted-fg))]">No actions available</span>
              ) : null}
            </div>
          ) : canShowRequestCancellation(request) ? (
            <div className="flex flex-wrap justify-end gap-1.5">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  openActionModal("CancelRequested", "Request cancellation", [request.requestId])
                }
              >
                Request cancellation
              </DsButton>
            </div>
          ) : (
            <p className="text-right text-xs text-[hsl(var(--ds-muted-fg))]">Already actioned</p>
          )}
        </footer>
      </article>
    );
  };

  const filtersActive = Boolean(
    itemSearchQuery.trim() || dateFrom || dateTo || selectedType !== ALL_TYPES
  );

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
        <div className="flex min-h-0 flex-1 flex-col p-4 lg:p-6">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] lg:flex-row">
            {/* Status sub-sidebar */}
            <aside className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/25 p-2 lg:w-52 lg:border-b-0 lg:border-r lg:p-3">
              <nav
                className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible"
                role="tablist"
                aria-orientation="vertical"
                aria-label="Status"
              >
                {STATUS_TABS.map((status) => {
                  const active = visibleStatus === status;
                  const count = statusCounts?.[status] ?? 0;
                  return (
                    <button
                      key={status}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setSelectedStatus(status)}
                      className={`flex shrink-0 items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition ${
                        active
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] shadow-[var(--ds-shadow-card)]"
                          : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-surface))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {STATUS_TAB_LABELS[status]}
                      <span className="text-xs tabular-nums">{count}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <section className="flex min-h-0 min-w-0 flex-1 flex-col">
              {/* Toolbar */}
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-2.5 sm:px-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Action Hub</p>
                  {filtersActive ? (
                    <p className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      {[
                        itemSearchQuery.trim() ? `“${itemSearchQuery.trim()}”` : null,
                        selectedType !== ALL_TYPES ? selectedType : null,
                        dateFrom || dateTo
                          ? `${dateFrom || "…"} → ${dateTo || "…"}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : (
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      Filter by employee, type, or dates
                    </p>
                  )}
                </div>
                <DsButton
                  type="button"
                  variant={filtersActive || showFilters ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(true)}
                >
                  <DsIcon icon={Filter} size="sm" />
                  Filters
                  {filtersActive ? (
                    <span className="ml-1 tabular-nums opacity-80">●</span>
                  ) : null}
                </DsButton>
                <DsButton type="button" variant="ghost" size="sm" onClick={refresh}>
                  <DsIcon icon={RefreshCw} size="sm" />
                </DsButton>
              </div>

              {/* Request type badges (sorted by pending count, desc) */}
              <div
                className="admin-shell-scrollbar flex shrink-0 gap-1.5 overflow-x-auto border-b border-[hsl(var(--ds-border))] px-3 py-2 sm:px-4"
                role="tablist"
                aria-label="Request type"
              >
                {typeBadges.map((badge) => {
                  const active = selectedType === badge.value;
                  return (
                    <button
                      key={badge.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setSelectedType(badge.value)}
                      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition ${
                        active
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {badge.label}
                      <span className="tabular-nums">{badge.pending}</span>
                    </button>
                  );
                })}
              </div>

              {displayedRequests.length > 0 ? (
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-2 sm:px-4">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      className="rounded border-[hsl(var(--ds-border))]"
                    />
                    {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
                  </label>
                  {canBulkAct ? (
                    <div className="inline-flex gap-1.5">
                      <DsButton
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => openActionModal("Approved", "Approve", selectedIds)}
                      >
                        Approve
                      </DsButton>
                      <DsButton
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => openActionModal("Rejected", "Reject", selectedIds)}
                      >
                        Reject
                      </DsButton>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Card grid */}
              <div className="relative min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
                {displayedRequests.length === 0 ? (
                  <div className="py-10">
                    <DsEmptyState
                      title="Nothing here"
                      description="No requests match this filter."
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {displayedRequests.map((request, index) => (
                      <div
                        key={String(request.requestId || `console-row-${index}`)}
                        className="flex min-w-0 flex-col [&>article]:flex-1"
                      >
                        {renderRequestCard(request)}
                      </div>
                    ))}
                  </div>
                )}
                {hasMore ? (
                  <div className="pointer-events-none sticky bottom-2 mt-4 flex justify-center">
                    <DsButton
                      type="button"
                      variant="primary"
                      size="sm"
                      className="pointer-events-auto shadow-lg"
                      onClick={() => setVisibleLimit((n) => n + PAGE_SIZE)}
                    >
                      Show more ·{" "}
                      <span className="tabular-nums">
                        {visibleRequests.length - displayedRequests.length}
                      </span>{" "}
                      left
                    </DsButton>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>

      {showFilters ? (
        <div
          className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => setShowFilters(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-filter-title"
            className="w-full max-w-lg overflow-hidden rounded-xl border-2 border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
              <div>
                <h3 id="activity-filter-title" className="text-base font-semibold text-[hsl(var(--ds-fg))]">
                  Filters
                </h3>
                <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                  Find requests by employee, type, or date range
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close filters"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <label className="block text-xs font-semibold text-[hsl(var(--ds-fg))]">
                Employee / code / email
                <div className="relative mt-1.5">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
                  />
                  <input
                    type="search"
                    value={itemSearchQuery}
                    onChange={(e) => setItemSearchQuery(e.target.value)}
                    placeholder="Name, employee code, or email…"
                    className={`${filterControlCls} w-full pl-8`}
                    autoFocus
                  />
                </div>
              </label>
              <label className="block text-xs font-semibold text-[hsl(var(--ds-fg))]">
                Request type
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className={`${filterControlCls} mt-1.5 w-full`}
                >
                  {typeBadges.map((badge) => (
                    <option key={badge.value} value={badge.value}>
                      {badge.label}
                      {badge.value === ALL_TYPES ? "" : ` (${badge.pending})`}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-semibold text-[hsl(var(--ds-fg))]">
                  From date
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={`${filterControlCls} mt-1.5 w-full`}
                  />
                </label>
                <label className="block text-xs font-semibold text-[hsl(var(--ds-fg))]">
                  To date
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={`${filterControlCls} mt-1.5 w-full`}
                  />
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/20 px-5 py-3.5">
              <button
                type="button"
                className="text-xs font-semibold text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                onClick={() => {
                  setItemSearchQuery("");
                  setSelectedType(ALL_TYPES);
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                Clear all
              </button>
              <DsButton type="button" variant="primary" size="sm" onClick={() => setShowFilters(false)}>
                Apply
              </DsButton>
            </div>
          </div>
        </div>
      ) : null}

      {actionModal.open && (
        <div className="fixed inset-0 z-[10025] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
                  {actionModal.title}
                  {actionModal.requestIds.length > 1
                    ? ` · ${actionModal.requestIds.length}`
                    : ""}
                </h3>
                {(() => {
                  const row = allRows.find(
                    (r) => r.requestId === actionModal.requestIds[0]
                  );
                  const chk = getChecklistNameHighlight(row);
                  if (!chk) return null;
                  return (
                    <p className="mt-1 truncate text-xs text-[hsl(var(--ds-muted-fg))]">
                      {chk}
                    </p>
                  );
                })()}
              </div>
              <button
                type="button"
                onClick={closeActionModal}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(var(--ds-border))]"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-3 p-4">
              {(() => {
                const row = allRows.find(
                  (r) => r.requestId === actionModal.requestIds[0]
                );
                const remarksLabel = getDeskRemarksLabel(row);
                const needRemarks = remarksRequiredForAction(
                  row,
                  actionModal.nextStatus
                );
                if (!remarksLabel) {
                  return (
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                      Confirm to continue. No remarks required.
                    </p>
                  );
                }
                return (
                  <label className="block text-xs font-medium text-[hsl(var(--ds-fg))]">
                    {remarksLabel}
                    {needRemarks ? (
                      <span className="text-[hsl(var(--ds-danger))]"> *</span>
                    ) : null}
                    <textarea
                      rows={4}
                      maxLength={REMARKS_MAX}
                      value={actionModal.remarks}
                      onChange={(e) =>
                        setActionModal((prev) => ({
                          ...prev,
                          remarks: e.target.value,
                        }))
                      }
                      className="mt-1.5 w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))]"
                      placeholder="Add a short note…"
                    />
                  </label>
                );
              })()}
              {getDeskRemarksLabel(
                allRows.find((r) => r.requestId === actionModal.requestIds[0])
              ) ? (
                <div className="text-right text-[10px] text-[hsl(var(--ds-muted-fg))]">
                  {(actionModal.remarks || "").length}/{REMARKS_MAX}
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-1">
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={closeActionModal}
                >
                  Cancel
                </DsButton>
                <DsButton
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={(() => {
                    const row = allRows.find(
                      (r) => r.requestId === actionModal.requestIds[0]
                    );
                    return (
                      remarksRequiredForAction(row, actionModal.nextStatus) &&
                      !(actionModal.remarks || "").trim()
                    );
                  })()}
                  onClick={confirmAction}
                >
                  Confirm
                </DsButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
